import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft, BadgeCheck, Ban, CalendarClock, CalendarX, ChevronLeft, ChevronRight, Clock3, Coffee,
  CupSoda, DoorClosed, Flag, FlagOff, ImageOff, KeyRound, PenLine, PlusCircle, Timer, UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import VisorArchivo from "@/components/VisorArchivo";
import { hoyISOBA } from "@/components/ClockBA";
import {
  Avatar, Badge, Card, FechaNav, Kpi, Modal, TONOS, btnPrim, btnSec, inputCls,
} from "@/components/rrhh/ui";

const TIPO_FICHADA = { entrada: "Entrada", salida: "Salida", desayuno: "Desayuno", almuerzo: "Almuerzo", merienda: "Merienda" };
const PAUSA_UI = {
  desayuno: { label: "Desayuno", icon: Coffee },
  almuerzo: { label: "Almuerzo", icon: UtensilsCrossed },
  merienda: { label: "Merienda", icon: CupSoda },
};
const DIA_DOT = {
  a_horario: "bg-[#2DD4BF]", tarde: "bg-amber-400", ausente: "bg-red-400",
  incompleta: "bg-violet-400", justificado: "bg-emerald-700", vacaciones: "bg-sky-400",
  licencia: "bg-fuchsia-400", feriado: "bg-gray-400",
};
const LEYENDA_MES = [
  ["a_horario", "A horario"], ["tarde", "Tarde"], ["ausente", "Ausente o licencia sin certificado"],
  ["incompleta", "Incompleta"], ["justificado", "Justificado"], ["vacaciones", "Vacaciones"], ["licencia", "Licencia"],
];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

export default function FichaEmpleadoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const fecha = params.get("fecha") || hoyISOBA();
  const setFecha = (f) => setParams({ fecha: f }, { replace: true });
  const [mesMini, setMesMini] = useState(null); // "aaaa-mm" del mini calendario
  const [fotoVer, setFotoVer] = useState(null);
  const [accion, setAccion] = useState(null);
  const [pinTemporal, setPinTemporal] = useState(null);

  const { data } = useQuery({
    queryKey: ["ficha", id, fecha],
    queryFn: async () => (await api.get(`/rrhh/empleados/${id}/ficha`, { params: { fecha } })).data,
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["ficha", id] });
    qc.invalidateQueries({ queryKey: ["hoy"] });
    qc.invalidateQueries({ queryKey: ["historial"] });
    qc.invalidateQueries({ queryKey: ["alertas-resumen"] });
    qc.invalidateQueries({ queryKey: ["alertas"] });
  };

  const verFoto = async (url) => {
    setFotoVer(url);
    try {
      await api.post("/rrhh/registrar-vista", { que: "foto de la jornada", referencia: url, empleado_id: id });
    } catch { /* el registro de vista no bloquea */ }
  };

  if (!data) {
    return <div className="py-16 text-center text-sm text-gray-500" data-testid="ficha-cargando">Cargando ficha…</div>;
  }

  const e = data.empleado;
  const k = data.kpis;
  const vigentes = data.fichadas.filter((f) => !f.anulada);
  const mesClave = mesMini || data.mes.clave;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-ficha-empleado">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-white" data-testid="ficha-volver">
        <ArrowLeft size={15} /> Volver
      </button>

      <Card testid="ficha-perfil" className="mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar iniciales={e.iniciales} size="xl" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white">{e.nombre} {e.apellido}</h1>
            <p className="mt-0.5 text-sm text-gray-400">
              <span className="font-mono-num">Legajo {e.legajo}</span> · Local base: <span className="uppercase">{e.local_base}</span>
            </p>
          </div>
          {!e.activo && <Badge color="rojo">Baja el {fmtFecha(e.fecha_baja)}</Badge>}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-white/8 pt-5 lg:grid-cols-4">
          <Dato icon={CalendarClock} label="Horario semanal" valor={`Lun a sáb · ${e.horario_entrada} a ${e.horario_salida}`} />
          <Dato icon={Coffee} label="Pausas y tolerancia" valor="50 min de pausas · 5 min de tolerancia" />
          <Dato icon={KeyRound} label="Estado del PIN"
                valor={<Badge color={e.pin_bloqueado ? "rojo" : "verde"} testid="ficha-pin">{e.pin_bloqueado ? "Bloqueado" : "Activo"}</Badge>} />
          <Dato icon={BadgeCheck} label="Alta" valor={<span className="font-mono-num">{fmtFecha(e.fecha_alta)}</span>} />
        </div>
      </Card>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-white">Detalle de la jornada</h2>
          <Badge color={data.estado.color} testid="ficha-estado-dia">{data.estado.label}</Badge>
          {data.novedad && <Badge color="violeta" testid="ficha-novedad">{data.novedad.replaceAll("_", " ")}</Badge>}
        </div>
        <FechaNav fecha={fecha} onChange={setFecha} testid="nav-fecha-ficha" />
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3.5 xl:grid-cols-5">
        <Kpi icon={Clock3} tone="verde" label="Tiempo trabajado" value={k.trabajado || "--"}
             sub={k.entrada_hora ? `Entrada ${k.entrada_hora}` : "Sin entrada"} testid="kpi-trabajado" />
        <Kpi icon={Coffee} tone="amarillo" label="Pausas" value={`${k.pausas_usados} / ${k.pausas_tope} min`}
             sub="Desayuno, almuerzo y merienda" testid="kpi-pausas" />
        <Kpi icon={Timer} tone="amarillo" label="Tardanza" value={`${k.tardanza_min} min`}
             sub={k.entrada_hora ? `Entró ${k.entrada_hora}` : "Sin entrada"} testid="kpi-tardanza" />
        <Kpi icon={PlusCircle} tone={k.adicional.cod === "si" ? "verde" : k.adicional.cod === "no" ? "rojo" : "gris"}
             label="Hora adicional" value={k.adicional.label} sub={k.adicional.detalle} testid="kpi-adicional" />
        <Kpi icon={CalendarX} tone="rojo" label="Tardanzas del mes"
             value={k.tardanzas_mes.cantidad > 0 ? String(k.tardanzas_mes.cantidad) : "Ninguna"}
             sub={`${k.tardanzas_mes.mes_label} · ${k.tardanzas_mes.minutos} min en total`} testid="kpi-tardanzas-mes" />
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <Card
            testid="ficha-timeline"
            titulo="Línea de tiempo"
            subtitulo={fmtFecha(fecha)}
            right={<Badge color="gris" testid="ficha-vigentes">{vigentes.length} fichadas vigentes</Badge>}
            bodyCls="p-3.5"
          >
            {data.fichadas.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-center">
                <CalendarX size={28} className="text-gray-600" />
                <p className="mt-3 text-sm font-semibold text-gray-300">Sin fichadas este día</p>
                <p className="text-xs text-gray-500">No fichó nada ese día.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.fichadas.map((f) => (
                  <div
                    key={f.id}
                    className={`flex items-center gap-3 rounded-xl border border-white/8 bg-[#0B0F14] px-4 py-3 ${f.anulada ? "opacity-45" : ""}`}
                    data-testid={`timeline-${f.id.slice(0, 8)}`}
                  >
                    <span className={`font-mono-num text-sm font-bold text-white ${f.anulada ? "line-through" : ""}`}>
                      {f.fecha_hora.slice(11, 16)}
                    </span>
                    <Badge color={f.tipo === "entrada" ? "verde" : f.tipo === "salida" ? "rojo" : "gris"}>
                      {TIPO_FICHADA[f.tipo]}
                    </Badge>
                    <span className="text-xs text-gray-500">{f.local_nombre}</span>
                    <span className="ml-auto flex items-center gap-2">
                      {f.anulada && <Badge color="rojo">Anulada</Badge>}
                      {f.origen !== "kiosco" && (
                        <span className="text-[11px] text-gray-500">
                          {f.origen === "rrhh_manual" ? "manual de RRHH" : f.origen === "rrhh" ? "corregida por RRHH" : f.origen}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card
            testid="ficha-fotos"
            titulo="Fotos de la jornada"
            subtitulo="De la entrada y la salida. Cada foto que se abre queda registrada con tu usuario."
          >
            <div className="grid grid-cols-2 gap-4">
              {[["entrada", data.fotos.entrada], ["salida", data.fotos.salida]].map(([tipo, foto]) => (
                <div key={tipo}>
                  {foto ? (
                    <button onClick={() => verFoto(foto.url)} className="block w-full" data-testid={`foto-${tipo}`}>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${foto.url}`}
                        alt={`Foto de ${tipo}`}
                        className="aspect-[4/3] w-full rounded-xl border border-white/10 object-cover transition-opacity hover:opacity-85"
                      />
                    </button>
                  ) : (
                    <div className="flex aspect-[4/3] flex-col items-center justify-center rounded-xl border border-dashed border-white/15 bg-[#0B0F14]" data-testid={`sin-foto-${tipo}`}>
                      <ImageOff size={22} className="text-gray-600" />
                      <p className="mt-2 text-xs text-gray-500">Sin {tipo}</p>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="font-semibold capitalize text-gray-300">{tipo}</span>
                    <span className="font-mono-num text-gray-500">{foto ? foto.hora : "Sin fichada"}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="ficha-pausas" titulo="Pausas" subtitulo="Cada una contra su propio tope. Terminan solas.">
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(PAUSA_UI).map(([tipo, ui]) => {
                const p = data.pausas[tipo];
                const Icon = ui.icon;
                return (
                  <div key={tipo} className="rounded-xl border border-white/8 bg-[#0B0F14] p-4" data-testid={`pausa-${tipo}`}>
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${TONOS.amarillo}`}>
                      <Icon size={15} />
                    </span>
                    <p className="mt-2.5 text-sm font-semibold text-white">{ui.label}</p>
                    <p className="mt-0.5 font-mono-num text-xs text-gray-300">{p.usado} / {p.tope} min</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">{p.hora ? `La tomó ${p.hora}` : "No la usó"}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card
            testid="ficha-acciones"
            titulo="Acciones de RRHH"
            subtitulo="Todas piden un motivo y una confirmación, y quedan en el historial."
            bodyCls="p-3.5"
          >
            <div className="space-y-2">
              <BotonAccion icon={KeyRound} tone="rojo" titulo="Blanquear el PIN"
                           linea="Si se lo olvidó. Sale un PIN temporal, que se ve una sola vez."
                           onClick={() => setAccion("blanquear")} testid="accion-blanquear-pin" />
              <BotonAccion icon={PenLine} tone="amarillo" titulo="Corregir la hora de una fichada"
                           linea="La original queda anulada, con su foto."
                           habilitada={data.acciones.corregir} onClick={() => setAccion("corregir")} testid="accion-corregir" />
              <BotonAccion icon={DoorClosed} tone="azul" titulo="Cerrar la jornada"
                           linea="La salida que se olvidó: manual, sin foto."
                           habilitada={data.acciones.cerrar} onClick={() => setAccion("cerrar")} testid="accion-cerrar" />
              <BotonAccion icon={Ban} tone="rojo" titulo="Anular una fichada"
                           linea="Queda en el registro, marcada como anulada."
                           habilitada={data.acciones.anular} onClick={() => setAccion("anular")} testid="accion-anular" />
              <BotonAccion icon={Flag} tone="violeta" titulo="Marcar una novedad"
                           linea="Justificado, tardanza justificada o injustificado."
                           habilitada={data.acciones.marcar_novedad} onClick={() => setAccion("novedad")} testid="accion-novedad" />
              <BotonAccion icon={FlagOff} tone="gris" titulo="Quitar la novedad"
                           linea={data.novedad ? `Hoy tiene: ${data.novedad.replaceAll("_", " ")}` : "Este día no tiene novedad."}
                           habilitada={data.acciones.quitar_novedad} onClick={() => setAccion("quitar")} testid="accion-quitar-novedad" />
            </div>
          </Card>

          <Card testid="ficha-mes" titulo="El mes" subtitulo="Tocá un día para ver su detalle." bodyCls="p-4">
            <MiniCalendario
              mesClave={mesClave}
              setMesClave={setMesMini}
              dias={data.mes.clave === mesClave ? data.mes.dias : null}
              mesLabel={data.mes.clave === mesClave ? data.mes.label : null}
              fecha={fecha}
              onPick={setFecha}
              empleadoId={id}
            />
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
              {LEYENDA_MES.map(([cod, label]) => (
                <span key={cod} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className={`h-2 w-2 rounded-full ${DIA_DOT[cod]}`} /> {label}
                </span>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {accion && (
        <ModalAccion
          accion={accion}
          data={data}
          fecha={fecha}
          onClose={() => setAccion(null)}
          onOk={(pin) => {
            setAccion(null);
            invalidar();
            if (pin) setPinTemporal(pin);
          }}
        />
      )}

      {pinTemporal && (
        <Modal titulo="PIN temporal generado" onClose={() => setPinTemporal(null)} testid="modal-pin-temporal">
          <p className="mb-4 text-sm text-gray-400">
            Se lo das a {e.nombre} y entra al kiosco con este PIN. <b className="text-white">Se ve una sola vez:</b> anotalo ahora.
          </p>
          <p className="rounded-2xl border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 py-5 text-center font-mono-num text-4xl font-bold tracking-[0.4em] text-[#2DD4BF]" data-testid="pin-temporal">
            {pinTemporal}
          </p>
          <div className="mt-5 flex justify-end">
            <button className={btnPrim} onClick={() => setPinTemporal(null)} data-testid="pin-temporal-ok">Listo, lo anoté</button>
          </div>
        </Modal>
      )}

      {fotoVer && <VisorArchivo url={fotoVer} onClose={() => setFotoVer(null)} titulo="Foto de la jornada" />}
    </motion.div>
  );
}

function Dato({ icon: Icon, label, valor }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONOS.gris}`}>
        <Icon size={16} />
      </span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
        <div className="mt-1 text-sm font-medium text-white">{valor}</div>
      </div>
    </div>
  );
}

function BotonAccion({ icon: Icon, tone, titulo, linea, habilitada = true, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={!habilitada}
      className={`flex w-full items-center gap-3 rounded-xl border border-white/8 bg-[#0B0F14] p-3 text-left transition-colors ${
        habilitada ? "hover:border-[#2DD4BF]/40" : "cursor-not-allowed opacity-40"
      }`}
      data-testid={testid}
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONOS[tone]}`}>
        <Icon size={16} />
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-white">{titulo}</span>
        <span className="block text-[11px] leading-snug text-gray-500">{linea}</span>
      </span>
    </button>
  );
}

function MiniCalendario({ mesClave, setMesClave, dias, mesLabel, fecha, onPick, empleadoId }) {
  const { data: dataMes } = useQuery({
    queryKey: ["ficha", empleadoId, `${mesClave}-15`],
    queryFn: async () => (await api.get(`/rrhh/empleados/${empleadoId}/ficha`, { params: { fecha: `${mesClave}-15` } })).data,
    enabled: !dias,
  });
  const diasFinal = dias || dataMes?.mes?.dias || {};
  const label = mesLabel || dataMes?.mes?.label || "";
  const [y, m] = mesClave.split("-").map(Number);
  const ndias = new Date(y, m, 0).getDate();
  const offset = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const mover = (d) => {
    const nd = new Date(y, m - 1 + d, 1);
    setMesClave(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
  };
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button onClick={() => mover(-1)} className="rounded-lg p-1 text-gray-400 hover:text-white" data-testid="mini-mes-anterior"><ChevronLeft size={15} /></button>
        <span className="text-[13px] font-bold capitalize text-white" data-testid="mini-mes-titulo">{label || `${MESES[m - 1]} ${y}`}</span>
        <button onClick={() => mover(1)} className="rounded-lg p-1 text-gray-400 hover:text-white" data-testid="mini-mes-siguiente"><ChevronRight size={15} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
          <span key={i} className="py-0.5 text-center text-[10px] font-semibold text-gray-600">{d}</span>
        ))}
        {Array.from({ length: offset }).map((_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: ndias }).map((_, i) => {
          const iso = `${mesClave}-${String(i + 1).padStart(2, "0")}`;
          const code = diasFinal[iso];
          const dot = DIA_DOT[code];
          const esHoy = iso === hoyISOBA();
          const activo = iso === fecha;
          return (
            <button
              key={iso}
              onClick={() => onPick(iso)}
              className={`flex h-8 flex-col items-center justify-center rounded-lg text-[11px] transition-colors ${
                activo ? "bg-[#2DD4BF]/15 text-[#2DD4BF]" : "text-gray-300 hover:bg-white/5"
              } ${esHoy ? "ring-1 ring-[#2DD4BF]" : ""}`}
              data-testid={`mini-dia-${iso}`}
            >
              <span>{i + 1}</span>
              <span className={`mt-0.5 h-1 w-1 rounded-full ${dot || "bg-transparent"}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ACCION_INFO = {
  blanquear: { titulo: "Blanquear el PIN", texto: "Se genera un PIN temporal de 4 dígitos y el bloqueo se levanta. El PIN se muestra una sola vez.", confirmar: "Blanquear el PIN" },
  corregir: { titulo: "Corregir la hora de una fichada", texto: "La fichada original queda anulada (con su foto) y se crea una nueva con la hora que indiques.", confirmar: "Corregir la hora" },
  cerrar: { titulo: "Cerrar la jornada", texto: "Se registra una salida manual a la hora de cierre, sin foto. La jornada se recalcula.", confirmar: "Cerrar la jornada" },
  anular: { titulo: "Anular una fichada", texto: "Queda en el registro, marcada como anulada, y la jornada se recalcula sin ella.", confirmar: "Anular la fichada" },
  novedad: { titulo: "Marcar una novedad", texto: "Cambia cómo cuenta el día en la liquidación.", confirmar: "Marcar la novedad" },
  quitar: { titulo: "Quitar la novedad", texto: "El día vuelve a calcularse solo con las fichadas que haya.", confirmar: "Quitar la novedad" },
};

function ModalAccion({ accion, data, fecha, onClose, onOk }) {
  const info = ACCION_INFO[accion];
  const [motivo, setMotivo] = useState("");
  const [fichadaId, setFichadaId] = useState("");
  const [horaNueva, setHoraNueva] = useState("");
  const [tipoNovedad, setTipoNovedad] = useState("justificado");
  const vigentes = data.fichadas.filter((f) => !f.anulada);

  const mut = useMutation({
    mutationFn: async () => {
      const id = data.empleado.id;
      if (accion === "blanquear") return api.post(`/rrhh/empleados/${id}/blanquear-pin`, { motivo });
      if (accion === "corregir") return api.post(`/rrhh/empleados/${id}/corregir-fichada`, { fichada_id: fichadaId, hora_nueva: horaNueva, motivo });
      if (accion === "cerrar") return api.post(`/rrhh/empleados/${id}/cerrar-jornada`, { fecha, motivo });
      if (accion === "anular") return api.post(`/rrhh/empleados/${id}/anular-fichada`, { fichada_id: fichadaId, motivo });
      if (accion === "novedad") return api.post(`/rrhh/empleados/${id}/novedad`, { fecha, tipo: tipoNovedad, motivo });
      if (accion === "quitar") return api.post(`/rrhh/empleados/${id}/quitar-novedad`, { fecha, motivo });
      return Promise.reject(new Error("acción desconocida"));
    },
    onSuccess: (r) => {
      toast.success(`${info.titulo}: listo`);
      onOk(accion === "blanquear" ? r.data.pin_temporal : null);
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const faltaFichada = (accion === "corregir" || accion === "anular") && !fichadaId;
  const faltaHora = accion === "corregir" && !horaNueva;

  return (
    <Modal titulo={info.titulo} onClose={onClose} testid={`modal-accion-${accion}`}>
      <p className="mb-4 text-sm text-gray-400">{info.texto}</p>
      <div className="space-y-3.5">
        {(accion === "corregir" || accion === "anular") && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fichada</label>
            <select value={fichadaId} onChange={(e) => setFichadaId(e.target.value)} className={`${inputCls} w-full`} data-testid="accion-fichada">
              <option value="">Elegí la fichada…</option>
              {vigentes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.fecha_hora.slice(11, 16)} · {TIPO_FICHADA[f.tipo]} · {f.local_nombre}
                </option>
              ))}
            </select>
          </div>
        )}
        {accion === "corregir" && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Hora correcta</label>
            <input type="time" value={horaNueva} onChange={(e) => setHoraNueva(e.target.value)} className={`${inputCls} w-full font-mono-num`} data-testid="accion-hora" />
          </div>
        )}
        {accion === "novedad" && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Novedad</label>
            <select value={tipoNovedad} onChange={(e) => setTipoNovedad(e.target.value)} className={`${inputCls} w-full`} data-testid="accion-tipo-novedad">
              <option value="justificado">Justificado — cuenta como día trabajado</option>
              <option value="tardanza_justificada" disabled={!data.kpis.tardanza_min}>Tardanza justificada — no pierde la adicional</option>
              <option value="injustificado">Injustificado — el día no suma</option>
            </select>
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Motivo (obligatorio)</label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            placeholder="ej. me lo pidió el encargado por WhatsApp"
            className={`${inputCls} w-full resize-none`}
            data-testid="accion-motivo"
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnSec} onClick={onClose}>Cancelar</button>
        <button
          className={btnPrim}
          disabled={mut.isPending || motivo.trim().length < 3 || faltaFichada || faltaHora}
          onClick={() => mut.mutate()}
          data-testid="accion-confirmar"
        >
          {info.confirmar}
        </button>
      </div>
    </Modal>
  );
}
